import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  InputBase,
  IconButton,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  CircularProgress,
  useTheme,
  Tooltip,
  Popper,
  Grow,
  ClickAwayListener,
  Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  ReceiptLong as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Payments as PaymentsIcon,
  People as PeopleIcon,
  AccountBalanceWallet as CashBookIcon,
  AccountBalance as BankBookIcon,
  InsertChart as InsightIcon,
  Description as DocumentIcon,
  Receipt,
  AttachMoney,
  AccountBalance,
  Person,
  Inventory,
  MonetizationOn as CashIcon,
  Store as VendorIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Download as DownloadIcon,
  Backup as BackupIcon,
  CalendarToday as CalendarIcon,
  LocalShipping as PurchaseIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
// import { collection, query, where, getDocs, limit, orderBy } from 'firebase-shim';
// import { db } from '../../firebase/config';
import indexedDBService from '../../utils/indexedDBService';
import { useAuth } from '../../context/AuthContext';
import useAnalytics from '../../hooks/useAnalytics';

// Define comprehensive navigation and section items
const mainNavItems = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, category: 'navigation' },
  { label: 'Sales', path: '/sales', icon: <ReceiptIcon />, category: 'navigation' },
  { label: 'Purchases', path: '/purchases', icon: <ShoppingCartIcon />, category: 'navigation' },
  { label: 'Invoices', path: '/invoices', icon: <DocumentIcon />, category: 'navigation' },
  { label: 'Inventory', path: '/inventory', icon: <InventoryIcon />, category: 'navigation' },
  { label: 'Clients', path: '/clients', icon: <PeopleIcon />, category: 'navigation' },
  { label: 'Vendors', path: '/vendors', icon: <PeopleIcon />, category: 'navigation' },
  { label: 'Cash Book', path: '/cashbook', icon: <CashBookIcon />, category: 'navigation' },
  { label: 'Bank Book', path: '/bankbook', icon: <BankBookIcon />, category: 'navigation' },
  { label: 'Transactions', path: '/transactions', icon: <PaymentsIcon />, category: 'navigation' },
  { label: 'Reports', path: '/reports', icon: <InsightIcon />, category: 'navigation' },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon />, category: 'settings' },
  { label: 'Profile', path: '/profile', icon: <Person />, category: 'settings' },
  { label: 'Business Setup', path: '/business-setup', icon: <BusinessIcon />, category: 'settings' },
];

// Define settings and configuration items
const settingsItems = [
  { label: 'General Settings', path: '/settings', icon: <SettingsIcon />, category: 'settings' },
  { label: 'Business Profile', path: '/profile', icon: <Person />, category: 'settings' },
  { label: 'Theme Settings', path: '/settings#theme', icon: <PaletteIcon />, category: 'settings' },
  { label: 'Notification Settings', path: '/settings#notifications', icon: <NotificationsIcon />, category: 'settings' },
  { label: 'Data Export', path: '/settings#export', icon: <DownloadIcon />, category: 'settings' },
  { label: 'Backup & Restore', path: '/settings#backup', icon: <BackupIcon />, category: 'settings' },
];

interface SearchResult {
  id: string;
  type: 'invoice' | 'transaction' | 'client' | 'vendor' | 'product' | 'purchase' | 'bank' | 'cash' | 'stock' | 'navigation' | 'settings' | 'date' | 'revenue' | 'expense';
  title: string;
  subtitle: string;
  route: string;
  data: any;
  category?: string;
  date?: string;
  amount?: number;
}

interface InlineSearchBarProps {
  isAdmin?: boolean;
}

const InlineSearchBar: React.FC<InlineSearchBarProps> = ({ isAdmin }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cachedData, setCachedData] = useState<{
    invoices: any[];
    transactions: any[];
    clients: any[];
    vendors: any[];
    inventory: any[];
    purchases: any[];
  }>({
    invoices: [],
    transactions: [],
    clients: [],
    vendors: [],
    inventory: [],
    purchases: []
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const searchInput = useRef<HTMLInputElement>(null);
  const popperAnchorRef = useRef<HTMLDivElement>(null);
  const { trackSearch } = useAnalytics();

  // Load and cache all data once (Skipped for Admins)
  const loadAllData = async () => {
    if (!currentUser || dataLoaded || isAdmin) return;

    try {
      setLoading(true);
      // ... (rest of loading logic)

      // Load all data in parallel
      const [invoices, transactions, clients, vendors, inventory, purchases] = await Promise.all([
        indexedDBService.getAllItems<any>('invoices'),
        indexedDBService.getAllItems<any>('transactions'),
        indexedDBService.getAllItems<any>('clients'),
        indexedDBService.getAllItems<any>('vendors'),
        indexedDBService.getAllItems<any>('inventory'),
        indexedDBService.getAllItems<any>('purchases')
      ]);

      setCachedData({
        invoices: invoices.slice(0, 100),
        transactions: transactions.slice(0, 200),
        clients: clients.slice(0, 100),
        vendors: vendors.slice(0, 100),
        inventory: inventory.slice(0, 100),
        purchases: purchases.slice(0, 100)
      });

      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (!isAdmin) {
      loadAllData();
    } else {
      setDataLoaded(true); // Treat as loaded for admin (no data needed)
    }
  }, [currentUser, isAdmin]);

  // Refresh data function (can be called when needed)
  const refreshData = () => {
    setDataLoaded(false);
    loadAllData();
  };

  // Handle navigating to a search result
  const handleNavigate = (path: string) => {
    navigate(path);
    setSearchTerm('');
    setIsOpen(false);
    setResults([]);
  };

  // Handle key navigation in search results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Navigate through search results with arrow keys
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prevIndex) =>
        Math.min(prevIndex + 1, results.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      const selected = results[selectedIndex];
      if (selected) {
        handleNavigate(selected.route);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setResults([]);
    }
  };

  // Fast client-side search function
  const performFastSearch = (term: string): SearchResult[] => {
    if (!dataLoaded) return [];

    const results: SearchResult[] = [];
    const lowerTerm = term.toLowerCase();

    // 0. Check if it's a math expression and calculate result
    if (isMathExpression(term)) {
      const calculation = calculateExpression(term);
      if (calculation !== null) {
        results.push({
          id: `calc-${term}`,
          type: 'navigation' as const, // Reuse navigation type for icon rendering if needed, or mapped to calculator
          title: `${term} = ${calculation}`,
          subtitle: `Calculator Result`,
          route: '#',
          data: { calculation, expression: term },
          category: 'calculator'
        });
      }
    }

    // Admin Search Logic
    if (isAdmin) {
      // 1. Admin Navigation
      const adminItems = [
        { label: 'Admin Overview', path: '/admin', icon: <DashboardIcon />, category: 'navigation' },
        { label: 'User Management', path: '/admin/users', icon: <PeopleIcon />, category: 'navigation' },
        { label: 'Resource Monitor', path: '/admin/resources', icon: <DashboardIcon />, category: 'navigation' },
        { label: 'Security Center', path: '/admin/security', icon: <SettingsIcon />, category: 'navigation' },
      ];

      const navResults = adminItems.filter(item =>
        item.label.toLowerCase().includes(lowerTerm)
      ).map(item => ({
        id: item.label,
        type: 'navigation' as const,
        title: item.label,
        subtitle: `Navigate to ${item.label}`,
        route: item.path,
        data: item,
        category: item.category
      }));
      results.push(...navResults);

      // 2. Search Users Action
      if (term.length > 1) {
        results.push({
          id: `search-user-${term}`,
          type: 'client', // Reuse icon
          title: `Find User: "${term}"`,
          subtitle: 'Search in User Management',
          route: `/admin/resources?search=${encodeURIComponent(term)}`, // Navigate to resources which has search
          data: { term },
          category: 'action'
        });
      }

      return results;
    }

    // 1. Search Navigation Items (instant)
    const navResults = mainNavItems.filter(item =>
      item.label.toLowerCase().includes(lowerTerm)
    ).map(item => ({
      id: item.label,
      type: 'navigation' as const,
      title: item.label,
      subtitle: `Navigate to ${item.label} `,
      route: item.path,
      data: item,
      category: item.category
    }));
    results.push(...navResults);

    // 2. Search Settings Items (instant)
    const settingsResults = settingsItems.filter(item =>
      item.label.toLowerCase().includes(lowerTerm)
    ).map(item => ({
      id: item.label,
      type: 'settings' as const,
      title: item.label,
      subtitle: `Settings: ${item.label} `,
      route: item.path,
      data: item,
      category: 'settings'
    }));
    results.push(...settingsResults);

    // 3. Search Invoices (from cache)
    cachedData.invoices.forEach(data => {
      const invoiceNumber = (data.invoiceNumber || '').toLowerCase();
      const clientName = (data.clientName || '').toLowerCase();
      const description = (data.description || '').toLowerCase();

      if (invoiceNumber.includes(lowerTerm) ||
        clientName.includes(lowerTerm) ||
        description.includes(lowerTerm)) {
        results.push({
          id: data.id,
          type: 'invoice',
          title: `Invoice #${data.invoiceNumber} `,
          subtitle: `${data.clientName} - ₹${data.total?.toFixed(2) || '0.00'} `,
          route: `/ invoices / ${data.id} `,
          data,
          amount: data.total,
          date: data.date
        });
      }
    });

    // 4. Search Transactions (from cache)
    cachedData.transactions.forEach(data => {
      const description = (data.description || '').toLowerCase();
      const category = (data.category || '').toLowerCase();
      const notes = (data.notes || '').toLowerCase();
      const type = (data.type || '').toLowerCase();
      const date = data.date || '';

      // Check if search term matches any field or if it's a date search
      const isDateSearch = isDateQuery(term);
      const matchesContent = description.includes(lowerTerm) ||
        category.includes(lowerTerm) ||
        notes.includes(lowerTerm) ||
        type.includes(lowerTerm);
      const matchesDate = isDateSearch && date.includes(term);

      if (matchesContent || matchesDate) {
        results.push({
          id: data.id,
          type: data.type === 'revenue' ? 'revenue' : data.type === 'expense' ? 'expense' : 'transaction',
          title: data.description || data.category || 'Transaction',
          subtitle: `${data.type} - ₹${data.amount?.toFixed(2) || '0.00'}${date ? ` - ${formatDate(date)}` : ''} `,
          route: `/ transactions / ${data.id} `,
          data,
          amount: data.amount,
          date: data.date,
          category: data.category
        });
      }
    });

    // 5. Search Clients (from cache)
    cachedData.clients.forEach(data => {
      const name = (data.name || '').toLowerCase();
      const gstin = (data.gstin || '').toLowerCase();
      const phone = data.phone || '';

      if (name.includes(lowerTerm) ||
        gstin.includes(lowerTerm) ||
        phone.includes(term)) {
        results.push({
          id: data.id,
          type: 'client',
          title: data.name,
          subtitle: `${data.gstin ? `GSTIN: ${data.gstin}` : ''} ${data.phone ? `Phone: ${data.phone}` : ''} `,
          route: `/ clients / ${data.id} `,
          data
        });
      }
    });

    // 6. Search Vendors (from cache)
    cachedData.vendors.forEach(data => {
      const name = (data.name || '').toLowerCase();
      const gstin = (data.gstin || '').toLowerCase();
      const phone = data.phone || '';

      if (name.includes(lowerTerm) ||
        gstin.includes(lowerTerm) ||
        phone.includes(term)) {
        results.push({
          id: data.id,
          type: 'vendor',
          title: data.name,
          subtitle: `${data.gstin ? `GSTIN: ${data.gstin}` : ''} ${data.phone ? `Phone: ${data.phone}` : ''} `,
          route: `/ vendors / ${data.id} `,
          data
        });
      }
    });

    // 7. Search Inventory/Stock Items (from cache)
    cachedData.inventory.forEach(data => {
      const name = (data.name || '').toLowerCase();
      const sku = (data.sku || '').toLowerCase();
      const hsn = data.hsn || '';

      if (name.includes(lowerTerm) ||
        sku.includes(lowerTerm) ||
        hsn.includes(term)) {
        results.push({
          id: data.id,
          type: 'stock',
          title: data.name,
          subtitle: `SKU: ${data.sku} | Stock: ${data.stock || 0} | ₹${data.price?.toFixed(2) || '0.00'} `,
          route: `/ inventory / ${data.id} `,
          data,
          amount: data.price
        });
      }
    });

    // 8. Search Purchases (from cache)
    cachedData.purchases.forEach(data => {
      const purchaseNumber = (data.purchaseNumber || '').toLowerCase();
      const vendorName = (data.vendorName || '').toLowerCase();
      const description = (data.description || '').toLowerCase();

      if (purchaseNumber.includes(lowerTerm) ||
        vendorName.includes(lowerTerm) ||
        description.includes(lowerTerm)) {
        results.push({
          id: data.id,
          type: 'purchase',
          title: `Purchase #${data.purchaseNumber} `,
          subtitle: `${data.vendorName} - ₹${data.total?.toFixed(2) || '0.00'} `,
          route: `/ purchases / ${data.id} `,
          data,
          amount: data.total,
          date: data.date
        });
      }
    });

    return results;
  };

  // Helper function to check if search term is a date query
  const isDateQuery = (term: string): boolean => {
    const datePatterns = [
      /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/, // DD-MM-YYYY or DD/MM/YYYY
      /^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}$/, // YYYY-MM-DD or YYYY/MM/DD
      /^\d{1,2}[-\/]\d{1,2}$/, // DD-MM or DD/MM
      /^\d{4}$/, // YYYY
      /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // Month names
      /^(january|february|march|april|may|june|july|august|september|october|november|december)/i, // Full month names
      /^(today|yesterday|tomorrow)/i, // Relative dates
      /^(this week|last week|next week)/i, // Week references
      /^(this month|last month|next month)/i, // Month references
      /^(this year|last year|next year)/i // Year references
    ];

    return datePatterns.some(pattern => pattern.test(term));
  };

  // Helper function to check if search term is a math expression
  const isMathExpression = (term: string): boolean => {
    // Check for basic arithmetic patterns
    const mathPatterns = [
      /^\d+[\+\-\*\/]\d+/, // Basic operations: 5+3, 10-2, 4*6, 15/3
      /^\d+[\+\-\*\/]\d+[\+\-\*\/]\d+/, // Multiple operations: 5+3*2
      /^\d+\.\d+[\+\-\*\/]\d+/, // Decimal operations: 5.5+2.3
      /^\d+[\+\-\*\/]\d+\.\d+/, // Decimal operations: 5+2.3
      /^\d+\.\d+[\+\-\*\/]\d+\.\d+/, // Decimal operations: 5.5+2.3
      /^\d+%/, // Percentage: 50%
      /^\d+% of \d+/, // Percentage of: 20% of 100
      /^\d+ \* \d+%/, // Percentage calculation: 100 * 20%
      /^\d+\^2/, // Square: 5^2
      /^\d+\^3/, // Cube: 5^3
      /^sqrt\(\d+\)/, // Square root: sqrt(25)
      /^\(\d+[\+\-\*\/]\d+\)/, // Parentheses: (5+3)
    ];

    return mathPatterns.some(pattern => pattern.test(term.trim()));
  };

  // Calculator function to evaluate math expressions
  const calculateExpression = (expression: string): number | null => {
    try {
      // Clean the expression
      let cleanExpr = expression.trim();

      // Handle percentage calculations
      if (cleanExpr.includes('% of ')) {
        const parts = cleanExpr.split('% of ');
        if (parts.length === 2) {
          const percentage = parseFloat(parts[0]);
          const number = parseFloat(parts[1]);
          return (percentage / 100) * number;
        }
      }

      if (cleanExpr.includes(' * ')) {
        const parts = cleanExpr.split(' * ');
        if (parts.length === 2 && parts[1].endsWith('%')) {
          const number = parseFloat(parts[0]);
          const percentage = parseFloat(parts[1].replace('%', ''));
          return (number * percentage) / 100;
        }
      }

      // Handle percentage only (convert to decimal)
      if (cleanExpr.endsWith('%')) {
        return parseFloat(cleanExpr.replace('%', '')) / 100;
      }

      // Handle square and cube
      if (cleanExpr.includes('^2')) {
        const base = parseFloat(cleanExpr.replace('^2', ''));
        return base * base;
      }

      if (cleanExpr.includes('^3')) {
        const base = parseFloat(cleanExpr.replace('^3', ''));
        return base * base * base;
      }

      // Handle square root
      if (cleanExpr.startsWith('sqrt(') && cleanExpr.endsWith(')')) {
        const number = parseFloat(cleanExpr.slice(5, -1));
        return Math.sqrt(number);
      }

      // Handle parentheses
      if (cleanExpr.includes('(') && cleanExpr.includes(')')) {
        // Simple parentheses handling
        cleanExpr = cleanExpr.replace(/\(/g, '').replace(/\)/g, '');
      }

      // Replace × with * and ÷ with /
      cleanExpr = cleanExpr.replace(/×/g, '*').replace(/÷/g, '/');

      // Evaluate the expression safely
      // Only allow numbers, basic operators, and parentheses
      if (/^[\d\+\-\*\/\.\(\)\s]+$/.test(cleanExpr)) {
        const result = Function(`"use strict"; return (${cleanExpr})`)();
        return typeof result === 'number' && !isNaN(result) ? result : null;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to format dates
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Fast real-time search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    // Show loading only if data is not loaded yet
    if (!dataLoaded) {
      setLoading(true);
      return;
    }

    // Perform instant client-side search
    const searchResults = performFastSearch(searchTerm);
    setResults(searchResults);
    setSelectedIndex(0);
    setLoading(false);

    // Track search for analytics
    trackSearch('global', searchTerm);
  }, [searchTerm, dataLoaded]);

  // Function to handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setResults([]);
    }
  };

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    // Special handling for calculator results
    if (result.category === 'calculator' && result.data?.calculation !== undefined) {
      // Copy the calculation result to clipboard
      navigator.clipboard.writeText(result.data.calculation.toString());
      // Update search term with the result
      setSearchTerm(result.data.calculation.toString());
      return;
    }

    // Navigate to the route for other results
    if (result.route !== '#') {
      navigate(result.route);
    }
    setSearchTerm('');
    setIsOpen(false);
    setResults([]);
  };

  // Close search when clicking outside
  const handleClickAway = () => {
    setIsOpen(false);
  };

  // Focus search on keyboard shortcut (Ctrl+K or /)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInput.current) {
          searchInput.current.focus();
        }
      }
      // Handle / key (but not if in an input/textarea)
      else if (e.key === '/' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName) &&
        !(e.target as HTMLElement).isContentEditable) {
        e.preventDefault();
        if (searchInput.current) {
          searchInput.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getIconByType = (type: string, category?: string) => {
    // Special handling for calculator results
    if (category === 'calculator') {
      return <AssessmentIcon />;
    }

    switch (type) {
      case 'invoice':
        return <Receipt />;
      case 'transaction':
        return <AttachMoney />;
      case 'client':
        return <Person />;
      case 'vendor':
        return <VendorIcon />;
      case 'stock':
        return <InventoryIcon />;
      case 'purchase':
        return <PurchaseIcon />;
      case 'bank':
        return <AccountBalance />;
      case 'cash':
        return <CashIcon />;
      case 'revenue':
        return <TrendingUpIcon />;
      case 'expense':
        return <AttachMoney />;
      case 'navigation':
        return <SearchIcon />;
      case 'settings':
        return <SettingsIcon />;
      case 'date':
        return <CalendarIcon />;
      default:
        return <SearchIcon />;
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 500, minWidth: 300 }}>
      <Paper
        ref={popperAnchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 44,
          borderRadius: 22,
          padding: '0 18px',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)} `,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          '&:hover': {
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
          },
          '&:focus-within': {
            boxShadow: '0 4px 15px rgba(0,0,0,0.12)',
            borderColor: theme.palette.primary.main,
          }
        }}
      >
        {loading ? (
          <CircularProgress size={16} sx={{ mr: 1 }} />
        ) : (
          <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
        )}
        <InputBase
          inputRef={searchInput}
          placeholder={isAdmin ? "Search users or pages..." : (dataLoaded ? "Search or calculate (5+3, 20% of 100)..." : "Loading data...")}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          fullWidth
          disabled={!dataLoaded}
          sx={{
            fontSize: '0.95rem',
            flex: 1,
            minWidth: 0,
            '&:focus': {
              outline: 'none'
            },
            '& input': {
              padding: '8px 0',
              '&::placeholder': {
                opacity: 0.7,
                fontSize: '0.9rem'
              }
            }
          }}
        />
        {searchTerm && (
          <IconButton
            size="small"
            onClick={() => {
              setSearchTerm('');
              setIsOpen(false);
              setResults([]);
            }}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <Tooltip title="Press '/' to search">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: '6px',
              backgroundColor: alpha(theme.palette.divider, 0.15),
              color: 'text.secondary',
              fontSize: '11px',
              fontFamily: 'monospace',
              fontWeight: 500,
              ml: 1.5,
              flexShrink: 0
            }}
          >
            /
          </Box>
        </Tooltip>
      </Paper>

      {/* Search Results Dropdown */}
      <Popper
        open={isOpen && (results.length > 0 || loading)}
        anchorEl={popperAnchorRef.current}
        placement="bottom-start"
        style={{ width: popperAnchorRef.current?.offsetWidth, zIndex: 1300 }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              sx={{
                mt: 1.5,
                maxHeight: 420,
                overflow: 'auto',
                boxShadow: theme.shadows[8],
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)} `,
              }}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : results.length > 0 ? (
                    <List disablePadding>
                      {results.map((result, index) => (
                        <ListItem
                          key={`${result.type} -${result.id} `}
                          button
                          selected={selectedIndex === index}
                          onClick={() => handleResultClick(result)}
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.08)'
                                : 'rgba(0,0,0,0.04)'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getIconByType(result.type, result.category)}
                          </ListItemIcon>
                          <ListItemText
                            primary={result.title}
                            secondary={result.subtitle}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : searchTerm ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        No results found for "{searchTerm}"
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default InlineSearchBar;
